import {
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as hijriMoment from 'moment-hijri';

interface HijriCell {
  day: number | null;
  gregorian: string | null;
  isSelected: boolean;
  isToday: boolean;
  isDisabled: boolean;
}

interface HijriMonth {
  value: number;
  labelAr: string;
  labelEn: string;
}

/**
 * Popup Hijri date picker.
 *
 * By default it stores and emits the Gregorian ISO date (YYYY-MM-DD) so existing
 * form logic stays Gregorian end-to-end, while presenting the user with a Hijri
 * calendar grid. Set `valueCalendar="hijri"` to emit/accept the Hijri date itself
 * (iYYYY-iMM-iDD) when the value must reach the API exactly as the user picked it,
 * with no calendar conversion.
 */
@Component({
  selector: 'app-hijri-calendar',
  templateUrl: './hijri-calendar.component.html',
  styleUrls: ['./hijri-calendar.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HijriCalendarComponent),
      multi: true,
    },
  ],
})
export class HijriCalendarComponent implements ControlValueAccessor {
  @Input() placeholder = '١٤٤٠/٠١/٠١';
  @Input() lang: 'ar' | 'en' = 'ar';
  /**
   * Calendar the control value is expressed in.
   * `gregorian` (default) → `YYYY-MM-DD`; `hijri` → `iYYYY-iMM-iDD` (no conversion).
   */
  @Input() valueCalendar: 'gregorian' | 'hijri' = 'gregorian';
  /**
   * Inclusive upper bound for selectable Gregorian dates.
   * Accepts either a JS Date or a `YYYY-MM-DD` string.
   * Used to enforce age policy (e.g. must be ≥ 18 years old).
   */
  @Input() set maxDate(value: Date | string | null | undefined) {
    if (!value) {
      this._maxDateIso = null;
      this.rebuildGrid();
      return;
    }
    if (typeof value === 'string') {
      this._maxDateIso = value.slice(0, 10);
    } else {
      const yyyy = value.getFullYear().toString().padStart(4, '0');
      const mm = (value.getMonth() + 1).toString().padStart(2, '0');
      const dd = value.getDate().toString().padStart(2, '0');
      this._maxDateIso = `${yyyy}-${mm}-${dd}`;
    }
    this.rebuildGrid();
  }
  private _maxDateIso: string | null = null;

  gregorianValue: string | null = null;
  hijriDisplay = '';

  viewYear: number;
  viewMonth: number;

  isOpen = false;
  disabled = false;

  weekdaysAr = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
  weekdaysEn = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  hijriMonths: HijriMonth[] = [
    { value: 1, labelAr: 'محرم', labelEn: 'Muharram' },
    { value: 2, labelAr: 'صفر', labelEn: 'Safar' },
    { value: 3, labelAr: 'ربيع الأول', labelEn: "Rabi' al-Awwal" },
    { value: 4, labelAr: 'ربيع الثاني', labelEn: "Rabi' al-Thani" },
    { value: 5, labelAr: 'جمادى الأولى', labelEn: 'Jumada al-Ula' },
    { value: 6, labelAr: 'جمادى الثانية', labelEn: 'Jumada al-Thaniyah' },
    { value: 7, labelAr: 'رجب', labelEn: 'Rajab' },
    { value: 8, labelAr: 'شعبان', labelEn: "Sha'ban" },
    { value: 9, labelAr: 'رمضان', labelEn: 'Ramadan' },
    { value: 10, labelAr: 'شوال', labelEn: 'Shawwal' },
    { value: 11, labelAr: 'ذو القعدة', labelEn: "Dhu al-Qa'dah" },
    { value: 12, labelAr: 'ذو الحجة', labelEn: 'Dhu al-Hijjah' },
  ];

  hijriYears: number[] = [];
  grid: HijriCell[][] = [];

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private host: ElementRef<HTMLElement>) {
    hijriMoment.locale('en');
    const today = hijriMoment();
    this.viewYear = Number(today.format('iYYYY'));
    this.viewMonth = Number(today.format('iM'));

    // Offer a 150-year window ending at the current Hijri year.
    const end = this.viewYear;
    const start = end - 150;
    this.hijriYears = Array.from({ length: end - start + 1 }, (_, i) => end - i);

    this.rebuildGrid();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
    }
  }

  get weekdays(): string[] {
    return this.lang === 'en' ? this.weekdaysEn : this.weekdaysAr;
  }

  writeValue(value: any): void {
    if (!value) {
      this.gregorianValue = null;
      this.hijriDisplay = '';
      this.rebuildGrid();
      return;
    }

    const asString =
      typeof value === 'string'
        ? value
        : value instanceof Date
        ? value.toISOString().slice(0, 10)
        : null;

    if (!asString) return;

    // In `hijri` mode the incoming value is already Hijri (iYYYY-iMM-iDD); a
    // Gregorian year (>= 1700) is still accepted so legacy/stored values work.
    const isHijriValue =
      this.valueCalendar === 'hijri' &&
      Number((asString.split('-')[0] || '').trim()) < 1700;
    const m = isHijriValue
      ? hijriMoment(asString, 'iYYYY-iM-iD')
      : hijriMoment(asString, 'YYYY-MM-DD');
    if (!m.isValid()) {
      this.gregorianValue = null;
      this.hijriDisplay = '';
      this.rebuildGrid();
      return;
    }

    this.gregorianValue = asString;
    this.hijriDisplay = m.format('iYYYY/iMM/iDD');
    this.viewYear = Number(m.format('iYYYY'));
    this.viewMonth = Number(m.format('iM'));
    this.rebuildGrid();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.isOpen = false;
    }
  }

  toggle() {
    if (this.disabled) return;
    if (!this.isOpen && !this.gregorianValue && this._maxDateIso) {
      const maxMoment = hijriMoment(this._maxDateIso, 'YYYY-MM-DD');
      if (maxMoment.isValid()) {
        this.viewYear = Number(maxMoment.format('iYYYY'));
        this.viewMonth = Number(maxMoment.format('iM'));
        this.rebuildGrid();
      }
    }
    this.isOpen = !this.isOpen;
    this.onTouched();
  }

  prevMonth() {
    if (this.disabled) return;
    this.viewMonth -= 1;
    if (this.viewMonth < 1) {
      this.viewMonth = 12;
      this.viewYear -= 1;
    }
    this.rebuildGrid();
  }

  nextMonth() {
    if (this.disabled) return;
    this.viewMonth += 1;
    if (this.viewMonth > 12) {
      this.viewMonth = 1;
      this.viewYear += 1;
    }
    this.rebuildGrid();
  }

  onMonthChange(value: number) {
    if (this.disabled) return;
    this.viewMonth = Number(value);
    this.rebuildGrid();
  }

  onYearChange(value: number) {
    if (this.disabled) return;
    this.viewYear = Number(value);
    this.rebuildGrid();
  }

  selectDay(cell: HijriCell, event: MouseEvent) {
    event.stopPropagation();
    if (this.disabled) return;
    if (!cell.day || !cell.gregorian || cell.isDisabled) return;

    this.gregorianValue = cell.gregorian;
    const m = hijriMoment(cell.gregorian, 'YYYY-MM-DD');
    this.hijriDisplay = m.format('iYYYY/iMM/iDD');
    this.onChange(
      this.valueCalendar === 'hijri' ? m.format('iYYYY-iMM-iDD') : this.gregorianValue
    );
    this.isOpen = false;
    this.rebuildGrid();
  }

  clear(event: MouseEvent) {
    event.stopPropagation();
    if (this.disabled) return;
    this.gregorianValue = null;
    this.hijriDisplay = '';
    this.onChange(null);
    this.rebuildGrid();
  }

  private rebuildGrid() {
    const first = hijriMoment(`${this.viewYear}-${this.viewMonth}-1`, 'iYYYY-iM-iD');
    if (!first.isValid()) {
      this.grid = [];
      return;
    }

    const startDow = first.day();
    const daysInMonth = (first as any).iDaysInMonth() as number;
    const todayGregorian = hijriMoment().format('YYYY-MM-DD');

    const cells: HijriCell[] = [];
    for (let i = 0; i < startDow; i++) {
      cells.push({ day: null, gregorian: null, isSelected: false, isToday: false, isDisabled: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const m = hijriMoment(`${this.viewYear}-${this.viewMonth}-${d}`, 'iYYYY-iM-iD');
      const greg = m.format('YYYY-MM-DD');
      cells.push({
        day: d,
        gregorian: greg,
        isSelected: this.gregorianValue === greg,
        isToday: greg === todayGregorian,
        isDisabled: this._maxDateIso !== null && greg > this._maxDateIso,
      });
    }
    while (cells.length % 7 !== 0) {
      cells.push({ day: null, gregorian: null, isSelected: false, isToday: false, isDisabled: false });
    }
    while (cells.length < 42) {
      cells.push({ day: null, gregorian: null, isSelected: false, isToday: false, isDisabled: false });
    }

    const rows: HijriCell[][] = [];
    for (let i = 0; i < cells.length; i += 7) {
      rows.push(cells.slice(i, i + 7));
    }
    this.grid = rows;
  }
}
